import React, { useCallback, useEffect, useRef } from 'react';

/**
 * « LES BASSINS » — la simulation d'usine, servie depuis `public/usine/`.
 *
 * ⚠️ MONTÉE UNE SEULE FOIS, JAMAIS DÉMONTÉE.
 * C'est une simulation à manches : recharger l'iframe relance `LB.boot()` et
 * repart de la manche I, écran d'ouverture compris. On la masque donc au lieu
 * de la retirer, et on la garde dans le flux (opacité + z-index, pas
 * `display:none`) pour que son `ResizeObserver` conserve la bonne taille de
 * canvas pendant qu'elle est cachée.
 *
 * Clavier : l'application a ses propres commandes (espace, d, p, a, r, n, +,
 * o, g, v, échap) qui recouvrent celles du diaporama (flèches, espace, échap).
 * Les deux ne se marchent pas dessus parce qu'elles vivent dans des documents
 * séparés : tant que l'iframe a le focus, elle seule reçoit les touches. D'où
 * le focus automatique à l'affichage, et le bouton de retour — rendu côté
 * parent — pour rendre le clavier au diaporama.
 *
 * ⚠️ PERFORMANCE : rester montée coûte cher si on n'y fait rien. La simulation
 * tourne sur une boucle `requestAnimationFrame` qui recalcule et redessine le
 * canvas à chaque frame, et `opacité: 0` ne l'arrête pas — le navigateur
 * continue de rendre un élément transparent. Le diaporama payait donc une
 * simulation complète sur les vingt-six slides où elle est invisible. On la met
 * explicitement en pause via `LB.setPaused` (même origine, donc accessible),
 * et on la réveille à l'affichage. L'état du moteur n'est pas touché.
 */
const UsineFrame = ({ visible, segment, onNavigate, onStatus, commandes }) => {
    const iframeRef = useRef(null);
    const navRef = useRef(onNavigate);
    navRef.current = onNavigate;
    const statusRef = useRef(onStatus);
    statusRef.current = onStatus;
    const visibleRef = useRef(visible);
    visibleRef.current = visible;

    /**
     * Demander à la séquence de scène d'avancer d'un cran ; `false` si elle ne
     * consomme pas l'appui — machine absente, en panne, ou passage terminé.
     *
     * ⚠️ C'EST LE SEUL ENDROIT QUI SAIT PARLER AU CONDUCTEUR, et il faut que ça
     * le reste. La règle de priorité — la séquence d'abord, la slide ensuite —
     * s'applique maintenant à DEUX entrées : la flèche du clavier, quand
     * l'iframe a le focus, et la flèche du PROMPTEUR, qui arrive par message et
     * n'a jamais eu de focus nulle part. Tant que la logique était en ligne dans
     * l'écouteur clavier, la seconde ne pouvait pas y accéder : depuis le
     * prompteur, la flèche droite sautait la visite du plateau et passait
     * directement à la slide suivante.
     *
     * Le garde sur `visible` sert au parent : il appelle sans savoir si la
     * simulation est à l'écran, et une slide ordinaire ne doit évidemment pas
     * voir sa flèche avalée par une machine cachée.
     */
    const avancerConducteur = useCallback(() => {
        if (!visibleRef.current) return false;
        try {
            const c = iframeRef.current?.contentWindow?.LB?.conductor;
            return !!(c?.active() && c.advance());
        } catch (err) {
            return false;   // simulation absente ou en panne : la flèche navigue
        }
    }, []);

    /* La commande est publiée dans une ref fournie par le parent plutôt que
       remontée par callback : le parent s'en sert au moment où il reçoit la
       flèche, pas au moment où elle change. */
    useEffect(() => {
        if (!commandes) return undefined;
        commandes.current = { avancer: avancerConducteur };
        return () => { commandes.current = null; };
    }, [commandes, avancerConducteur]);

    /* Diagnostic de chargement.
       La slide dessine un repli — des cuves statiques — au cas où la simulation
       ne chargerait pas. Tant que ce repli s'affichait par défaut, on le voyait
       passer pendant les 300 ms de fondu : des cuves d'un autre style juste
       avant la manche I. Il ne doit apparaître QUE sur un échec avéré, donc on
       vérifie que la machine a bien démarré (son canvas existe) et on prévient
       le parent. Le garde-temps couvre le cas où `load` ne vient jamais. */
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        const verdict = (ok) => statusRef.current?.(ok);
        const machineDemarree = () => {
            try {
                return !!iframe.contentWindow?.document?.querySelector('canvas');
            } catch (err) {
                return false;
            }
        };

        // Avant le `load`, l'absence de canvas ne prouve rien : on ne conclut à
        // l'échec que sur un `load` improductif, une erreur, ou l'expiration du
        // garde-temps. Sinon on afficherait le repli pendant le chargement.
        if (machineDemarree()) verdict(true);

        const apresChargement = () => verdict(machineDemarree());
        const enErreur = () => verdict(false);
        iframe.addEventListener('load', apresChargement);
        iframe.addEventListener('error', enErreur);

        const garde = setTimeout(apresChargement, 8000);
        return () => {
            clearTimeout(garde);
            iframe.removeEventListener('load', apresChargement);
            iframe.removeEventListener('error', enErreur);
        };
    }, []);

    /* Les flèches doivent sortir de l'iframe.
       La simulation ne les utilise pas (`main.js` ne gère que espace, entrée,
       échap, d/p/a/r/n/+/o/g/v), mais tant qu'elle a le focus les touches ne
       quittent pas son document : les flèches du diaporama ne partaient nulle
       part. Et sur ses pages de présentation — dont la clôture, à la fin de la
       manche III — elle avale toutes les autres touches et sort : plus rien ne
       répondait, ni l'usine ni le diaporama. On était piégé, avec pour seule
       issue un bouton à la souris.

       Même origine, donc on peut écouter directement dans l'iframe et renvoyer
       la navigation au parent.

       ⚠️ LA FLÈCHE DROITE EST PARTAGÉE, et l'ordre de priorité est ici.
       Le MODE CONDUCTEUR (public/usine/src/ui/conductor.js) donne à la machine une
       séquence de scène — visite du plateau, tours, bilan — qui se déroule à la
       même touche que le diaporama. On demande donc d'abord à la séquence si elle
       consomme l'appui ; elle ne rend la main que lorsque le passage est fini, et
       la flèche redevient alors « slide suivante ». Le présentateur n'a jamais à
       savoir laquelle des deux il pilote : c'est tout l'objet du dispositif.

       La flèche GAUCHE, elle, sort toujours. Elle n'est pas le symétrique de la
       droite et ne rembobine pas la machine — c'est l'issue de secours, la seule
       touche qui rende à coup sûr la main au diaporama si la séquence se bloque. */
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        let win = null;
        const onKey = (e) => {
            // PageDown / PageUp : les touches de la télécommande de scène,
            // traitées exactement comme les flèches.
            const avant = e.key === 'ArrowRight' || e.key === 'PageDown';
            if (!avant && e.key !== 'ArrowLeft' && e.key !== 'PageUp') return;
            e.preventDefault();
            // Consommée par la séquence : on ne change pas de slide.
            // Maj+→ la court-circuite — même geste que le « ⏭ passer » du
            // prompteur, pour que le raccourci de répétition marche aussi quand
            // on est resté sur la machine.
            if (avant && !e.shiftKey && avancerConducteur()) return;
            // Rendre le focus au parent : ensuite, tout redevient natif.
            iframe.blur();
            window.focus();
            navRef.current?.(avant ? 1 : -1);
        };

        const attach = () => {
            try {
                win?.removeEventListener('keydown', onKey);
                win = iframe.contentWindow;
                win?.addEventListener('keydown', onKey);
            } catch (err) {
                // Iframe pas encore prête : le `load` rejouera.
            }
        };
        attach();
        iframe.addEventListener('load', attach);

        return () => {
            iframe.removeEventListener('load', attach);
            try { win?.removeEventListener('keydown', onKey); } catch (err) { /* document parti */ }
        };
    }, [avancerConducteur]);

    // Pause / reprise de la boucle de rendu de la simulation.
    useEffect(() => {
        const apply = () => {
            try {
                iframeRef.current?.contentWindow?.LB?.setPaused?.(!visible);
            } catch (e) {
                // Iframe pas encore prête : le `load` ci-dessous rejouera.
            }
        };
        apply();

        const iframe = iframeRef.current;
        iframe?.addEventListener('load', apply);
        return () => iframe?.removeEventListener('load', apply);
    }, [visible]);

    /* MODE CONDUCTEUR : chaque slide « usine » annonce le passage qu'elle ouvre.
       `enter()` amène la machine dans l'état que cette slide promet — la visite du
       plateau pour la première, la manche sous IA pour la deuxième, la manche
       rééquilibrée et déjà réglée pour la troisième — puis la flèche droite
       déroule la suite.

       ⚠️ L'appel est RÉPÉTÉ à chaque affichage, y compris quand le présentateur
       revient d'une slide en arrière et repart. Chaque appel remet la machine
       dans l'état de départ du passage (voir `resetTo` dans main.js) : un
       aller-retour de slide rejoue donc le passage depuis son début, quel que
       soit l'endroit où on l'avait laissé.

       Le `setTimeout` sert d'abord au focus — sans lui, la bascule d'opacité n'est
       pas finie et le focus part dans le vide, donc le clavier reste au parent. */
    useEffect(() => {
        if (!visible || !iframeRef.current) return;
        let timer = 0;
        /* Deux raisons de réessayer plutôt que de tenter une seule fois : la
           première slide « usine » peut s'afficher avant que l'iframe ait fini de
           charger — c'est le cas si le présentateur ouvre la conférence
           directement sur cette slide pendant une répétition — et un `enter()`
           perdu laisserait la machine sur son écran d'ouverture, sans que la
           flèche puisse plus rien y faire. On réessaie pendant quatre secondes,
           puis on abandonne : `←` reste l'issue. */
        let restant = 16;
        const armer = () => {
            iframeRef.current?.focus();
            if (!segment) return;
            try {
                const c = iframeRef.current?.contentWindow?.LB?.conductor;
                if (c) { c.enter(segment); return; }
            } catch (err) {
                // Simulation pas prête ou en panne : la slide affiche son repli.
            }
            if (restant-- > 0) timer = setTimeout(armer, 250);
        };
        timer = setTimeout(armer, 120);
        return () => clearTimeout(timer);
    }, [visible, segment]);

    return (
        /* Plein cadre, PAR-DESSUS le bandeau du diaporama (qui est en z-[60]).
           L'application a son propre titre et son propre fond quadrillé : la
           laisser démarrer sous le bandeau empilait deux en-têtes — celui du
           diaporama rognait celui de l'app — et encadrait un fond quadrillé
           dans un autre. Pendant l'usine, elle occupe tout l'écran ; c'est un
           basculement de support, pas une vignette. */
        <div
            className={`absolute inset-0 transition-opacity duration-300 ${visible ? 'opacity-100 z-[70]' : 'opacity-0 -z-10 pointer-events-none'
                }`}
        >
            <iframe
                ref={iframeRef}
                src="/usine/index.html"
                title="Les Bassins — simulation d'usine"
                className="w-full h-full rounded-2xl bg-slate-950"
            />

            {/* ⚠️ IL N'Y A PLUS DE BOUTON « ← diaporama » (retiré le 08/09).
                Il datait d'avant le mode conducteur, quand la simulation avalait
                les flèches et qu'il était la SEULE façon de ressortir. Ce n'est
                plus vrai : le gestionnaire de touches ci-dessus intercepte les
                flèches dans l'iframe, et `←` rend la main au diaporama quoi
                qu'il arrive. Le bouton ne servait donc plus qu'à une chose —
                s'afficher en haut à droite d'un écran projeté, sur les trois
                passages de l'usine, pendant douze minutes.
                Si tu le remets un jour : il lui faut son `blur()` au clic
                (c'était l'objet de la prop `onExit`), sinon il garde le focus
                et « espace » le re-déclenche au lieu d'avancer d'une slide. */}
        </div>
    );
};

export default UsineFrame;
